import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { uploadService, dataService } from '../services/api';
import { useNotification } from '../contexts/NotificationContext';
import DataTable from '../components/ui/DataTable';
import {
  ArrowLeftIcon,
  DatabaseIcon,
  CalendarIcon,
  FileTextIcon,
  BarChart3Icon,
  TrendingUpIcon,
  InfoIcon,
  GitBranchIcon,
  EyeIcon,
  AlertTriangleIcon,
  DownloadIcon
} from 'lucide-react';

// Custom hook for dataset data fetching
function useDatasetData(uploadId) {
  const [dataset, setDataset] = useState(null);
  const [versions, setVersions] = useState([]);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { notify } = useNotification();

  const fetchDatasetData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all uploads to find this dataset
      const uploadsResponse = await uploadService.getUploads();
      if (Array.isArray(uploadsResponse)) {
        // Find the current dataset
        const currentDataset = uploadsResponse.find(upload => upload.upload_id === uploadId);
        setDataset(currentDataset);

        // Fetch datasets for proper versioning (like DataSources.js does)
        const datasetData = await uploadService.getDatasets();
        const datasets = datasetData.datasets || datasetData || {};

        // Find versions using the same logic as DataSources.js
        if (currentDataset && currentDataset.dataset_name) {
          const datasetVersions = datasets[currentDataset.dataset_name] || [];
          setVersions(datasetVersions);
        }
      }

      // Preview data will be loaded separately when Preview tab is accessed

    } catch (err) {
      console.error('Error fetching dataset data:', err);
      setError('Failed to load dataset information');
      // Remove notify call to avoid infinite loop from dependency
    } finally {
      setLoading(false);
    }
  }, [uploadId]);

  useEffect(() => {
    if (uploadId) {
      fetchDatasetData();
    }
  }, [uploadId, fetchDatasetData]);

  const fetchPreviewData = useCallback(async () => {
    if (!uploadId) return;

    try {
      setPreviewLoading(true);
      setPreviewError(null);

      // Try to get rich preview with statistics (non-quick mode)
      try {
        console.log('Attempting rich preview for dataset:', uploadId);
        const richPreview = await dataService.getDatasetPreview(uploadId, null, false);

        if (richPreview.error) {
          throw new Error(richPreview.error);
        }

        setPreviewData(richPreview);
        console.log('Successfully loaded rich preview with statistics');
        return;

      } catch (richError) {
        console.warn('Rich preview failed, trying quick mode:', richError.message);

        // Fallback to quick mode (no statistics)
        try {
          const quickPreview = await dataService.getDatasetPreview(uploadId, null, true);

          if (quickPreview.error) {
            throw new Error(quickPreview.error);
          }

          setPreviewData(quickPreview);
          console.log('Successfully loaded quick preview (no statistics)');
          return;

        } catch (quickError) {
          console.warn('Quick preview also failed:', quickError.message);
          throw quickError;
        }
      }

    } catch (err) {
      console.error('Failed to load dataset preview:', err);
      setPreviewError(err.message || 'Failed to load dataset preview');
      setPreviewData(null);
    } finally {
      setPreviewLoading(false);
    }
  }, [uploadId]);

  return {
    dataset,
    versions,
    previewData,
    previewLoading,
    previewError,
    loading,
    error,
    refetch: fetchDatasetData,
    fetchPreviewData
  };
}

function DatasetDetails() {
  const { uploadId } = useParams();
  const navigate = useNavigate();
  const { notify } = useNotification();
  const [activeTab, setActiveTab] = useState('summary');

  const {
    dataset,
    versions,
    previewData,
    previewLoading,
    previewError,
    loading,
    error,
    fetchPreviewData
  } = useDatasetData(uploadId);

  // Load preview data when Preview tab is accessed
  const handlePreviewTabClick = () => {
    setActiveTab('preview');
    if (!previewData && !previewLoading) {
      fetchPreviewData();
    }
  };

  // Download dataset
  const handleDownloadDataset = async () => {
    try {
      notify.info('Preparing download...');
      await uploadService.downloadDataset(uploadId);
      notify.success('Download started');
    } catch (error) {
      console.error('Download error:', error);
      notify.error('Failed to download dataset');
    }
  };

  // Helper functions
  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    if (bytes === 0) return '0 Bytes';
    const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch (e) {
      return dateStr || 'Unknown';
    }
  };

  const getDisplayName = (dataset) => {
    // Use dataset_name if available (primary)
    if (dataset?.dataset_name) {
      return dataset.dataset_name;
    }
    
    // Fallback to files array filename
    if (dataset?.files && dataset.files.length > 0 && dataset.files[0].filename) {
      return dataset.files[0].filename;
    }
    
    // Other fallbacks
    if (dataset?.file_name) {
      return dataset.file_name;
    }
    
    if (dataset?.filename) {
      return dataset.filename;
    }
    
    return `Dataset ${uploadId.slice(0, 8)}`;
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="py-12 text-center">
          <div className="animate-spin h-6 w-6 border-2 border-slate-300 dark:border-slate-600 border-t-slate-600 dark:border-t-slate-300 rounded-full mx-auto mb-4"></div>
          <p className="text-slate-600 dark:text-slate-400">Loading dataset details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center mb-8">
          <button
            className="flex items-center text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-all duration-200 transform hover:scale-105"
            onClick={() => navigate('/data-sources')}
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to Data Sources
          </button>
        </div>
        <div className="text-center py-16 px-4">
          <AlertTriangleIcon className="mx-auto h-12 w-12 text-red-500 mb-4" />
          <p className="text-slate-700 dark:text-slate-300">{error}</p>
        </div>
      </div>
    );
  }

  if (!dataset) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center mb-8">
          <button
            className="flex items-center text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-all duration-200 transform hover:scale-105"
            onClick={() => navigate('/data-sources')}
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to Data Sources
          </button>
        </div>
        <div className="text-center py-16 px-4">
          <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
            Dataset not found
          </h3>
          <p className="text-slate-600 dark:text-slate-400">
            The requested dataset could not be found.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center mb-8">
        <button
          className="flex items-center text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-all duration-200 transform hover:scale-105"
          onClick={() => navigate('/data-sources')}
        >
          <ArrowLeftIcon className="w-4 h-4 mr-2" />
          Back to Data Sources
        </button>
      </div>

      {/* Dataset Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-xl bg-primary-100 dark:bg-slate-800/50 flex items-center justify-center">
              <DatabaseIcon className="w-6 h-6 text-primary-600 dark:text-silver-400" />
            </div>
            <div>
              <h1 className="text-3xl font-semibold text-slate-900 dark:text-white">
                {getDisplayName(dataset)}
              </h1>
              <div className="flex items-center space-x-3 mt-2">
                {/* Status badge - descriptive with icons */}
                {dataset?.status && (
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${
                    dataset.status === 'ready' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-400' :
                    dataset.status === 'processing' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-400' :
                    dataset.status === 'pending' ? 'bg-gray-100 text-gray-800 dark:bg-gray-900/50 dark:text-gray-400' :
                    dataset.status === 'failed' ? 'bg-rose-100 text-rose-800 dark:bg-rose-900/50 dark:text-rose-400' :
                    'bg-gray-100 text-gray-800 dark:bg-gray-900/50 dark:text-gray-400'
                  }`}>
                    {dataset.status === 'ready' && 'Ready'}
                    {dataset.status === 'processing' && 'Processing'}
                    {dataset.status === 'pending' && 'Pending'}
                    {dataset.status === 'failed' && 'Failed'}
                    {!['ready', 'processing', 'pending', 'failed'].includes(dataset.status) && dataset.status}
                  </span>
                )}
                {/* Type badge - only show for ready or processing datasets */}
                {dataset?.type && dataset.type !== 'unknown' && (dataset?.status === 'ready' || dataset?.status === 'processing') && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-400">
                    {dataset.type === 'tabular' && 'Tabular Data'}
                    {dataset.type === 'images' && 'Images'}
                    {dataset.type === 'text_corpus' && 'Text Corpus'}
                    {dataset.type === 'time_series' && 'Time Series'}
                    {!['tabular', 'images', 'text_corpus', 'time_series'].includes(dataset.type) && dataset.type}
                  </span>
                )}
                <span className="text-sm text-slate-500 dark:text-slate-400">
                  Created {formatDate(dataset?.created_at)}
                </span>
              </div>
            </div>
          </div>
          <div>
            <button
              onClick={handleDownloadDataset}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
            >
              <DownloadIcon className="w-4 h-4" />
              Download
            </button>
          </div>
        </div>
      </div>

      {/* Tabs for different sections */}
      <div>
        <div className="border-b border-slate-200 dark:border-slate-700 mb-8">
          <div className="flex space-x-8">
            <button
              className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center transition-all duration-200 transform hover:scale-105 ${
                activeTab === 'summary'
                  ? 'border-primary-600 text-primary-600 dark:border-primary-500 dark:text-primary-500'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300 dark:text-slate-400 dark:hover:text-slate-300 dark:hover:border-slate-500'
              }`}
              onClick={() => setActiveTab('summary')}
            >
              <InfoIcon className="w-4 h-4 mr-2" />
              Summary
            </button>
            <button
              className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center transition-all duration-200 transform hover:scale-105 ${
                activeTab === 'versions'
                  ? 'border-primary-600 text-primary-600 dark:border-primary-500 dark:text-primary-500'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300 dark:text-slate-400 dark:hover:text-slate-300 dark:hover:border-slate-500'
              }`}
              onClick={() => setActiveTab('versions')}
            >
              <GitBranchIcon className="w-4 h-4 mr-2" />
              Versions
            </button>
            <button
              className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center transition-all duration-200 transform hover:scale-105 ${
                activeTab === 'preview'
                  ? 'border-primary-600 text-primary-600 dark:border-primary-500 dark:text-primary-500'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300 dark:text-slate-400 dark:hover:text-slate-300 dark:hover:border-slate-500'
              }`}
              onClick={handlePreviewTabClick}
            >
              <EyeIcon className="w-4 h-4 mr-2" />
              Preview
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="min-h-[400px]">
          {activeTab === 'summary' && (
            <div className="space-y-6">
              {/* Processing Status Alert - only if not ready */}
              {dataset?.status && dataset.status !== 'ready' && (
                <div className={`rounded-lg p-4 border ${
                  dataset.status === 'processing' ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800' :
                  dataset.status === 'pending' ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800' :
                  dataset.status === 'failed' ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' :
                  'bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-800'
                }`}>
                  <div className="flex items-start">
                    <div className={`flex-shrink-0 ${
                      dataset.status === 'processing' ? 'text-amber-600 dark:text-amber-400' :
                      dataset.status === 'pending' ? 'text-blue-600 dark:text-blue-400' :
                      dataset.status === 'failed' ? 'text-red-600 dark:text-red-400' :
                      'text-gray-600 dark:text-gray-400'
                    }`}>
                      {dataset.status === 'processing' && <TrendingUpIcon className="w-5 h-5" />}
                      {dataset.status === 'pending' && <CalendarIcon className="w-5 h-5" />}
                      {dataset.status === 'failed' && <AlertTriangleIcon className="w-5 h-5" />}
                    </div>
                    <div className="ml-3 flex-1">
                      <h3 className={`text-sm font-medium ${
                        dataset.status === 'processing' ? 'text-amber-800 dark:text-amber-200' :
                        dataset.status === 'pending' ? 'text-blue-800 dark:text-blue-200' :
                        dataset.status === 'failed' ? 'text-red-800 dark:text-red-200' :
                        'text-gray-800 dark:text-gray-200'
                      }`}>
                        {dataset.status === 'processing' && 'Dataset is being processed'}
                        {dataset.status === 'pending' && 'Dataset processing pending'}
                        {dataset.status === 'failed' && 'Processing failed'}
                      </h3>
                      <p className={`mt-1 text-sm ${
                        dataset.status === 'processing' ? 'text-amber-700 dark:text-amber-300' :
                        dataset.status === 'pending' ? 'text-blue-700 dark:text-blue-300' :
                        dataset.status === 'failed' ? 'text-red-700 dark:text-red-300' :
                        'text-gray-700 dark:text-gray-300'
                      }`}>
                        {dataset.status === 'processing' && 'Running automatic validation and structure discovery to make the data queryable. This usually takes 1-3 minutes.'}
                        {dataset.status === 'pending' && 'File may not have finished uploading yet, or processing has not started. Please wait or check back shortly.'}
                        {dataset.status === 'failed' && (dataset.error_message || 'An error occurred during processing. Check validation details below.')}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Dataset Information */}
              <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-6">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-4">
                  Dataset Information
                </h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">Type:</span>
                    <span className="text-gray-900 dark:text-white">
                      {dataset?.type === 'tabular' ? 'Tabular Dataset (CSV/Parquet)' :
                       dataset?.type === 'images' ? 'Image Collection' :
                       dataset?.type === 'text_corpus' ? 'Text Corpus' :
                       dataset?.type === 'time_series' ? 'Time Series Data' :
                       dataset?.type || 'Unknown'}
                    </span>
                  </div>
                  {dataset?.file_format && (
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">File Format:</span>
                      <span className="px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                        {dataset.file_format.toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">Size:</span>
                    <span className="text-gray-900 dark:text-white">{formatFileSize(dataset?.size)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">Created:</span>
                    <span className="text-gray-900 dark:text-white">{formatDate(dataset?.created_at)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">Dataset ID:</span>
                    <span className="text-gray-900 dark:text-white font-mono text-xs">{uploadId}</span>
                  </div>
                </div>
              </div>

              {/* Validation Issues - ONLY if there are errors or warnings */}
              {dataset && (dataset.validation_errors || dataset.validation_warnings) && (
                <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-6 border border-amber-200 dark:border-amber-800">
                  <div className="flex items-center mb-4">
                    <AlertTriangleIcon className="w-5 h-5 mr-2 text-amber-600 dark:text-amber-400" />
                    <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-100 uppercase tracking-wider">
                      Validation Issues
                    </h3>
                  </div>

                  {/* Errors */}
                  {dataset.validation_errors && dataset.validation_errors.trim() && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-red-700 dark:text-red-400 mb-2">Errors:</h4>
                      <ul className="list-disc list-inside space-y-1 text-sm text-red-600 dark:text-red-400">
                        {dataset.validation_errors.split('|').map((error, i) => (
                          <li key={i}>{error.trim()}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Warnings */}
                  {dataset.validation_warnings && dataset.validation_warnings.trim() && (
                    <div>
                      <h4 className="text-sm font-medium text-amber-700 dark:text-amber-400 mb-2">Warnings:</h4>
                      <ul className="list-disc list-inside space-y-1 text-sm text-amber-600 dark:text-amber-400">
                        {dataset.validation_warnings.split('|').map((warning, i) => (
                          <li key={i}>{warning.trim()}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* CSV Delimiter Suggestion */}
                  {dataset.suggested_delimiter && (
                    <div className="mt-3 pt-3 border-t border-amber-200 dark:border-amber-700">
                      <div className="text-sm text-amber-700 dark:text-amber-300">
                        <span className="font-medium">Suggested delimiter:</span>
                        <code className="ml-2 px-2 py-1 bg-amber-100 dark:bg-amber-900/40 rounded text-xs font-mono">
                          '{dataset.suggested_delimiter}'
                        </code>
                      </div>
                    </div>
                  )}
                </div>
              )}

            </div>
          )}

          {activeTab === 'versions' && (
            <div>
              <div className="flex items-center mb-4">
                <GitBranchIcon className="w-5 h-5 mr-2 text-gray-500 dark:text-gray-400" />
                <h2 className="text-xl font-semibold">Dataset Versions</h2>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                All versions of this dataset, sorted by creation time.
              </p>

              {versions.length > 0 ? (
                <div className="space-y-3">
                  {versions.map((version, index) => (
                    <div
                      key={version.upload_id}
                      className="flex items-center justify-between p-4 bg-gray-50 dark:bg-slate-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-600/50 transition-colors"
                    >
                      <div className="flex items-center space-x-4 flex-1">
                        <div className="flex items-center space-x-3">
                          <div className="w-6 h-6 flex items-center justify-center">
                            {version.is_latest && (
                              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                            )}
                          </div>
                          <DatabaseIcon className="w-5 h-5 text-gray-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <p className="font-medium text-gray-900 dark:text-white">
                              {version.files && version.files[0] ? version.files[0].filename : 
                               version.version_description || `Version ${index + 1}`}
                            </p>
                            {version.upload_id === uploadId && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
                                Current
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {version.version_description && `${version.version_description} • `}
                            Created: {formatDate(version.created_at)}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                          {version.size ? formatFileSize(version.size) : ''}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-slate-500 dark:text-slate-400">No versions found for this dataset.</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'preview' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <EyeIcon className="w-5 h-5 mr-2 text-gray-500 dark:text-gray-400" />
                  <h2 className="text-xl font-semibold">Data Preview</h2>
                </div>
                {previewData && (
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {previewData.row_count ? `${previewData.row_count.toLocaleString()} rows` : ''}
                    {previewData.columns ? ` • ${previewData.columns.length} columns` : ''}
                  </div>
                )}
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                Interactive preview with sample data and column statistics.
              </p>

              {previewLoading ? (
                <div className="text-center py-16 px-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                  <div className="animate-spin h-8 w-8 border-2 border-slate-300 dark:border-slate-600 border-t-primary-600 dark:border-t-primary-500 rounded-full mx-auto mb-4"></div>
                  <p className="text-slate-600 dark:text-slate-400">Loading dataset preview...</p>
                  <p className="text-sm text-slate-500 dark:text-slate-500 mt-2">
                    Computing statistics and sample data
                  </p>
                </div>
              ) : previewError ? (
                <div className="text-center py-16 px-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                  <AlertTriangleIcon className="mx-auto h-12 w-12 text-red-500 mb-4" />
                  <p className="text-red-700 dark:text-red-400 font-medium">Failed to load preview</p>
                  <p className="text-sm text-red-600 dark:text-red-500 mt-2">{previewError}</p>
                  <button
                    onClick={fetchPreviewData}
                    className="mt-4 px-4 py-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-md hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
                  >
                    Retry
                  </button>
                </div>
              ) : previewData ? (
                <div className="space-y-8">
                  {/* Dataset Overview */}
                  <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-6">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-slate-900 dark:text-white">
                          {previewData.row_count?.toLocaleString() || 'N/A'}
                        </div>
                        <div className="text-sm text-slate-600 dark:text-slate-400">Total Rows</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-slate-900 dark:text-white">
                          {previewData.columns?.length || 0}
                        </div>
                        <div className="text-sm text-slate-600 dark:text-slate-400">Columns</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-slate-900 dark:text-white">
                          {previewData.column_stats?.length || 0}
                        </div>
                        <div className="text-sm text-slate-600 dark:text-slate-400">With Statistics</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-slate-900 dark:text-white">
                          {previewData.sample_rows?.length || 0}
                        </div>
                        <div className="text-sm text-slate-600 dark:text-slate-400">Sample Rows</div>
                      </div>
                    </div>
                  </div>

                  {/* Sample Data Table */}
                  {previewData.sample_rows && previewData.sample_rows.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold mb-4 flex items-center">
                        <FileTextIcon className="w-5 h-5 mr-2 text-gray-500" />
                        Sample Data
                      </h3>
                      <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-600 overflow-hidden">
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-600">
                            <thead className="bg-slate-50 dark:bg-slate-700">
                              <tr>
                                {previewData.columns?.map((column) => (
                                  <th key={column} className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                    <div className="flex items-center space-x-2">
                                      <span>{column}</span>
                                      <span className="text-xs bg-slate-200 dark:bg-slate-600 px-1 rounded">
                                        {previewData.dtypes?.[column] || 'unknown'}
                                      </span>
                                    </div>
                                  </th>
                                )) || []}
                              </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-slate-800 divide-y divide-slate-200 dark:divide-slate-600">
                              {previewData.sample_rows.map((row, index) => (
                                <tr key={index} className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                                  {previewData.columns?.map((column) => (
                                    <td key={column} className="px-4 py-3 whitespace-nowrap text-sm text-slate-900 dark:text-slate-100">
                                      {row[column] !== null && row[column] !== undefined ? String(row[column]) : (
                                        <span className="text-slate-400 italic">null</span>
                                      )}
                                    </td>
                                  )) || []}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Column Statistics */}
                  {previewData.column_stats && previewData.column_stats.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold mb-4 flex items-center">
                        <BarChart3Icon className="w-5 h-5 mr-2 text-gray-500" />
                        Column Statistics
                      </h3>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {previewData.column_stats.map((colStat) => (
                          <div key={colStat.column_name} className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-600 p-4">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="font-medium text-slate-900 dark:text-slate-100">{colStat.column_name}</h4>
                              <span className="text-xs bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded">
                                {colStat.data_type}
                              </span>
                            </div>

                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-slate-600 dark:text-slate-400">Non-null:</span>
                                <span className="font-mono">{colStat.non_null_count?.toLocaleString() || 'N/A'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-600 dark:text-slate-400">Unique:</span>
                                <span className="font-mono">{colStat.unique_count?.toLocaleString() || 'N/A'}</span>
                              </div>

                              {/* Numeric Statistics */}
                              {colStat.mean !== null && colStat.mean !== undefined && (
                                <>
                                  <div className="flex justify-between">
                                    <span className="text-slate-600 dark:text-slate-400">Mean:</span>
                                    <span className="font-mono">{Number(colStat.mean).toFixed(2)}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-slate-600 dark:text-slate-400">Std Dev:</span>
                                    <span className="font-mono">{colStat.std ? Number(colStat.std).toFixed(2) : 'N/A'}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-slate-600 dark:text-slate-400">Min:</span>
                                    <span className="font-mono">{colStat.min !== null ? Number(colStat.min).toFixed(2) : 'N/A'}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-slate-600 dark:text-slate-400">Max:</span>
                                    <span className="font-mono">{colStat.max !== null ? Number(colStat.max).toFixed(2) : 'N/A'}</span>
                                  </div>
                                  {colStat.median !== null && colStat.median !== undefined && (
                                    <div className="flex justify-between">
                                      <span className="text-slate-600 dark:text-slate-400">Median:</span>
                                      <span className="font-mono">{Number(colStat.median).toFixed(2)}</span>
                                    </div>
                                  )}
                                </>
                              )}

                              {/* String Statistics */}
                              {colStat.min_length !== null && colStat.min_length !== undefined && (
                                <>
                                  <div className="flex justify-between">
                                    <span className="text-slate-600 dark:text-slate-400">Min Length:</span>
                                    <span className="font-mono">{colStat.min_length}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-slate-600 dark:text-slate-400">Max Length:</span>
                                    <span className="font-mono">{colStat.max_length}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-slate-600 dark:text-slate-400">Avg Length:</span>
                                    <span className="font-mono">{colStat.avg_length ? Number(colStat.avg_length).toFixed(1) : 'N/A'}</span>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* No Statistics Message */}
                  {(!previewData.column_stats || previewData.column_stats.length === 0) && (
                    <div className="text-center py-8 px-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                      <InfoIcon className="mx-auto h-8 w-8 text-yellow-500 mb-2" />
                      <p className="text-yellow-700 dark:text-yellow-400">
                        Column statistics are not available for this dataset.
                      </p>
                      <p className="text-sm text-yellow-600 dark:text-yellow-500 mt-1">
                        This may be due to quick mode or large dataset size.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-16 px-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                  <EyeIcon className="mx-auto h-12 w-12 text-slate-400 mb-4" />
                  <p className="text-slate-500 dark:text-slate-400">
                    Click to load dataset preview
                  </p>
                  <button
                    onClick={fetchPreviewData}
                    className="mt-4 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    Load Preview
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DatasetDetails;